import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useApplication } from '../../../services/application';
import { useTrans } from '../../../services/i18n';
import { useAlertContext } from '../../../contexts/AlertContext';
import { catchYupErrors } from '../../../utils/validationUtils';

const FLAG_FIELDS = [
  'pursuitNotices',
  'savedSearchNotices',
  'teamUpdates',
  'onEmailNotifications',
  'taskReminders',
  'pursuitDailyRecap',
  'savedSearchDailyRecap',
  'teamUpdatesDailyRecap',
  'taskRemindersDailyRecap',
  'personalTaskRemindersDailyRecap',
];

const flag = PropTypes.oneOfType([PropTypes.bool, PropTypes.number]);

export const notificationSettingsUserShape = PropTypes.shape({
  pursuitNotices: flag,
  savedSearchNotices: flag,
  teamUpdates: flag,
  onEmailNotifications: flag,
  taskReminders: flag,
  pursuitDailyRecap: flag,
  savedSearchDailyRecap: flag,
  teamUpdatesDailyRecap: flag,
  taskRemindersDailyRecap: flag,
  personalTaskRemindersDailyRecap: flag,
  crmUpdates: flag,
  crmEmailFrequency: PropTypes.string,
  crmDailyRecap: flag,
});

export function buildNotificationSettingsPayload(values, crmAvailable, recapAvailable) {
  const payload = { ...values };
  FLAG_FIELDS.forEach((name) => {
    payload[name] = values[name] ? 1 : 0;
  });
  if (crmAvailable) {
    payload.crmUpdates = values.crmUpdates ? 1 : 0;
    payload.crmEmailFrequency = values.crmEmailFrequency;
  } else {
    delete payload.crmUpdates;
    delete payload.crmEmailFrequency;
  }
  if (recapAvailable) {
    payload.crmDailyRecap = values.crmDailyRecap ? 1 : 0;
  } else {
    delete payload.crmDailyRecap;
  }
  return payload;
}

export function useNotificationSwitches(user, values, crmAvailable, recapAvailable) {
  const [updateNoti, setUpdateNoti] = useState(false);
  const [dailyRecapEmails, setDailyRecapEmails] = useState(false);

  useEffect(() => {
    if (
      Boolean(user?.savedSearchNotices) ||
      Boolean(user?.taskReminders) ||
      (crmAvailable && Boolean(user?.crmUpdates))
    ) {
      setUpdateNoti(true);
    }
    if (
      Boolean(user?.pursuitDailyRecap) ||
      Boolean(user?.savedSearchDailyRecap) ||
      Boolean(user?.teamUpdatesDailyRecap) ||
      Boolean(user?.taskRemindersDailyRecap) ||
      Boolean(user?.personalTaskRemindersDailyRecap) ||
      (recapAvailable && Boolean(user?.crmDailyRecap))
    ) {
      setDailyRecapEmails(true);
    }
  }, [user, crmAvailable, recapAvailable]);

  useEffect(() => {
    setUpdateNoti(Boolean(values?.savedSearchNotices || values?.taskReminders || (crmAvailable && values?.crmUpdates)));
  }, [values, crmAvailable]);

  useEffect(() => {
    setDailyRecapEmails(
      Boolean(
        values?.pursuitDailyRecap ||
          values?.savedSearchDailyRecap ||
          values?.teamUpdatesDailyRecap ||
          values?.taskRemindersDailyRecap ||
          values?.personalTaskRemindersDailyRecap ||
          (recapAvailable && values?.crmDailyRecap)
      )
    );
  }, [values, recapAvailable]);

  return { updateNoti, dailyRecapEmails };
}

export function useNotificationSettingsSubmit({ crmAvailable, recapAvailable, onUpdate, onClose, onFailure }) {
  const application = useApplication();
  const { trans } = useTrans();
  const { successAlert, errorAlert } = useAlertContext();
  const [pending, setPending] = useState(false);

  const onSubmit = useCallback(
    (values, { setStatus, setErrors }) => {
      setPending(true);
      application
        .call(
          'user.setEmailNotificationSettings',
          buildNotificationSettingsPayload(values, crmAvailable, recapAvailable)
        )
        .then(
          (result) => {
            successAlert(trans('notifications-updated-successfully'));
            onUpdate(result);
            onClose?.();
          },
          (error) => {
            errorAlert(trans('notification-settings-update-failed'));
            onFailure?.();
            catchYupErrors(error, setErrors, (err) => setStatus({ message: err.message }));
          }
        )
        .finally(() => {
          setPending(false);
        });
    },
    [application, crmAvailable, recapAvailable, errorAlert, onClose, onFailure, onUpdate, successAlert, trans]
  );

  return { onSubmit, pending };
}
